import { Box, Stack } from "@mui/material";
import type { UseMutationResult } from "@tanstack/react-query";
import { useLocation, useNavigate } from "@tanstack/react-router";
import type React from "react";
import { createContext, useContext, useEffect, useState } from "react";
import { useAuthStore } from "../../store/auth-store";
import {
	apiHooks,
	parseServerErrorMessage,
	type Schemas,
} from "../apis/norsk-gassnett-api-hooks";
import Loader from "../components/atomics/loader";

interface AuthState {
	isAuthenticated: boolean;
	token: string | null;
	startLoginFlow: (
		options: Schemas["StartLoginCustomerFlowRequestBody"],
	) => Promise<
		{ flowId: string; error: undefined } | { flowId: undefined; error: string }
	>;
	startLoginFlowMutation: UseMutationResult<
		Schemas["StartLoginCustomerFlowResponseBody"],
		Error
	>;
	completeLoginFlow: (
		options: Schemas["CompleteLoginCustomerFlowRequestBody"] & {
			redirectTo?: string;
		},
	) => Promise<
		{ token: string; error: undefined } | { token: undefined; error: string }
	>;
	completeLoginFlowMutation: UseMutationResult<
		Schemas["CompleteLoginCustomerFlowResponseBody"],
		Error
	>;
	switchIdentity: (id: string) => Promise<void>;
	switchIdentityMutation: UseMutationResult<
		Schemas["SwitchCustomerResponseBody"],
		Error
	>;

	logout: () => void;
}

const AuthContext = createContext<AuthState | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
	const { token, setToken, resetState } = useAuthStore();
	const reauthenticate = apiHooks.useQuery(
		"get",
		"/v1/identity/reauthenticate",
		undefined,
		{
			enabled: !!token,
			refetchInterval: 1000 * 60 * 10,
			retry: 1,
			refetchIntervalInBackground: true,
			refetchOnWindowFocus: false,
		},
	);
	const startLoginFlowMutation = apiHooks.useMutation(
		"post",
		"/v1/auth/start-login-flow",
	);
	const completeLoginFlowMutation = apiHooks.useMutation(
		"post",
		"/v1/auth/complete-login-flow",
	);

	const switchIdentityMutation = apiHooks.useMutation(
		"post",
		"/v1/identity/switch",
	);

	const location = useLocation();
	const navigate = useNavigate();

	const [redirectTo, setRedirectTo] = useState<string>();

	useEffect(() => {
		if (reauthenticate.isLoading || reauthenticate.isPending) {
			return;
		} else if (reauthenticate.error) {
			setToken(null);
		} else if (reauthenticate.data?.data?.token) {
			setToken(reauthenticate.data.data.token);
		}
	}, [reauthenticate, setToken]);

	useEffect(() => {
		if (!token && !location.pathname.startsWith("/logginn")) {
			navigate({ to: "/logginn", search: { redirectTo } });
			return;
		}
		if (token && location.pathname.startsWith("/logginn")) {
			navigate({ to: redirectTo || "/" });
			return;
		}
	}, [token, location, navigate, redirectTo]);

	// Show loading state while checking auth for the first time
	if (reauthenticate.isLoading) {
		return (
			<Box
				sx={{
					display: "flex",
					minHeight: "100vh",
					flexDirection: "column",
				}}
			>
				<Stack
					justifyContent={"center"}
					alignItems="center"
					direction="column"
					sx={{ width: "100%", flexGrow: 1 }}
				>
					<Loader variant="container" />
				</Stack>
			</Box>
		);
	}

	const startLoginFlow: AuthState["startLoginFlow"] = async (options) => {
		try {
			const res = await startLoginFlowMutation.mutateAsync({
				body: { identifier: options.identifier },
			});
			return { flowId: res.data.flowId, error: undefined };
		} catch (e) {
			return { flowId: undefined, error: parseServerErrorMessage(e) };
		}
	};

	const completeLoginFlow: AuthState["completeLoginFlow"] = async (options) => {
		try {
			const res = await completeLoginFlowMutation.mutateAsync({
				body: { flowId: options.flowId, code: options.code },
			});

			setRedirectTo(options.redirectTo);
			setToken(res.data.token);

			return { token: res.data.token, error: undefined };
		} catch (e) {
			return { token: undefined, error: parseServerErrorMessage(e) };
		}
	};

	const switchIdentity: AuthState["switchIdentity"] = async (id: string) => {
		const res = await switchIdentityMutation.mutateAsync({
			body: { bcId: id },
		});
		if (res.data.token) {
			setToken(res.data.token);
		}
	};

	const logout = () => {
		resetState();
	};

	return (
		<AuthContext.Provider
			value={{
				isAuthenticated: !!token,
				token,
				startLoginFlow,
				// @ts-expect-error We are expecting this as the type is not 100% correct, but that is fine
				startLoginFlowMutation: startLoginFlowMutation,
				completeLoginFlow,
				// @ts-expect-error We are expecting this as the type is not 100% correct, but that is fine
				completeLoginFlowMutation: completeLoginFlowMutation,
				switchIdentity: switchIdentity,
				// @ts-expect-error We are expecting this as the type is not 100% correct, but that is fine
				switchIdentityMutation: switchIdentityMutation,
				logout,
			}}
		>
			{children}
		</AuthContext.Provider>
	);
}

export function useAuthContext() {
	const context = useContext(AuthContext);
	if (context === undefined) {
		throw new Error("useAuth must be used within an AuthProvider");
	}
	return context;
}
